import { Chevron } from "@/assets/icons";
import { useClickOutside } from "@/hooks";
import { flip, offset, useFloating } from "@floating-ui/react";
import clsx from "clsx";
import { find, map } from "lodash";
import {
  Fragment,
  // MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Checkbox } from "../checkbox";
import styles from "./styles.scss";
import { CustomDropdownProps } from "./types";

export const CustomDropdown = <J extends string = string>({
  id: dropdownId,
  placeholder,
  selectedOption,
  options,
  onChange,
  onClearAll,
  label,
  disabled,
  triggerClassName,
  containerClassName,
  menuClassName,
  itemClassName,
  onCloseMenu,
  isNeedToHighlightSelectedItem,
  mandatory,
  ...props
}: CustomDropdownProps<J>) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!isOpen && onCloseMenu) {
      onCloseMenu(dropdownId);
    }
  }, [dropdownId, isOpen, onCloseMenu]);

  const {
    x,
    y,
    strategy,
    update,
    refs: { setReference, setFloating },
  } = useFloating({
    placement: "bottom",
    middleware: [flip(), offset(4)],
  });

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  useClickOutside(containerRef, handleClose);

  const handleTriggerClick = useCallback(() => {
    update();
    setIsOpen(previous => !previous);
  }, [update]);

  return (
    <div
      ref={containerRef}
      className={clsx(styles.container, containerClassName)}
    >
      {label && (
        <div className={styles.label}>
          {label}
          {mandatory && <span className={styles.asterix}> *</span>}
        </div>
      )}

      <button
        ref={setReference}
        type="button"
        onClick={handleTriggerClick}
        className={clsx(
          styles.trigger,
          !selectedOption && styles.placeholderShown,
          selectedOption.length && styles.selected,
          triggerClassName,
        )}
        disabled={disabled}
        {...props}
      >
        {selectedOption.length >= 1 ? (
          <>
            {map(selectedOption, (option, key) => {
              return (
                <Fragment key={key}>
                  {+key !== 0 ? ", " : ""}
                  {option?.DisplayName ?? option?.Value}
                </Fragment>
              );
            })}
            <div className={styles.annotation}>
              <span className={styles.annotationDivider}>|</span>
              <span className={styles.annotationValue}>
                {selectedOption.length}
              </span>
            </div>
          </>
        ) : (
          <span className={clsx(styles.text, styles.placeholder)}>
            {placeholder}
          </span>
        )}

        <Chevron width={24} height={24} className={styles.chevron} />
      </button>
      {isOpen && (
        <div
          ref={setFloating}
          style={{
            position: strategy,
            top: y ?? "",
            left: x ?? "",
            width: "250px",
          }}
        >
          <ul className={clsx(styles.menu, menuClassName)}>
            {options.length ? (
              options.map(option => {
                const isSelectOption = Boolean(
                  find(selectedOption, selected => {
                    return selected.Id === option.Id;
                  }),
                );

                const { Id, Value, DisplayName } = option;

                const handleItemClick = () => {
                  // TODO Fix me
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-expect-error
                  onChange({ ...option }, dropdownId);
                };

                return (
                  <li
                    key={`${Id}`}
                    className={clsx(
                      styles.menuItem,
                      itemClassName,
                      isNeedToHighlightSelectedItem &&
                        Id === selectedOption?.Id &&
                        styles.selectedHighlightedItem,
                    )}
                    onClick={handleItemClick}
                  >
                    <span
                      style={{
                        pointerEvents: "none",
                      }}
                    >
                      <Checkbox
                        id={`${Id}`}
                        name={`${Id}`}
                        checked={isSelectOption}
                        onChange={() => {}}
                      />
                    </span>

                    <span>{DisplayName ?? Value}</span>
                  </li>
                );
              })
            ) : (
              <li className={clsx(styles.menuItem, itemClassName)}>
                There are no options
              </li>
            )}
          </ul>
          {options.length && (
            <div className={styles.menuActions}>
              <div />
              <span onClick={onClearAll} className={styles.clearAll}>
                Clear All
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
